// From forge.ts
export interface ComponentState {
	[hookIndex: number]: any;
}

export interface RenderState {
	[componentKey: string]: ComponentState;
}

export interface Handler {
	componentKey: string;
	prop: string;
}

export type ForgeProps = { [key: string]: any };

export interface ForgeDoc {
	children: ForgeDoc[];
	// Number aligns with the current major version of forge-react.
	// This is used to handle which set of UI Kit 2 component versions we will render.
	// For example, version 10 of @forge/react will contain code-generated components based off of Atlassian Design System components
	// These components contain new behaviour and accept different props compared to their version 9 counterparts.
	// Version 10 of @forge/react will only render these new components. Version 9 and below will continue to render the older implementations
	forgeReactMajorVersion?: number;
	// caching the change detection result between the previous and current props
	// so that we avoid re-computing the deep comparison of props on the nested
	// element unnecessarily.
	hasChanged?: boolean;
	key?: string;
	props?: ForgeProps;
	reconciliationCount?: number;
	type: string;
}

export type ForgeElement<P = Record<string, any>> = PrimitiveElement<P> | FunctionElement<P>;

export interface PrimitiveElement<P = Record<string, any>> {
	// After @types/react@17.0.65, `key` is now only string | null
	// (see: https://github.com/DefinitelyTyped/DefinitelyTyped/pull/66735)
	// Previously it was string | number | null
	// Because @forge/ui and AFE use different versions of @types/react,
	// we need to use `any` here to avoid type errors on both sides.
	key: any;
	props: P & { children: ForgeNode[] };
	type: string;
}

export interface FunctionElement<P = Record<string, any>> {
	key: any;
	props: P & { children: ForgeNode[] };
	type: (props: P) => ForgeElement;
}

export const isForgeElement = (auxNode: ForgeNode): auxNode is ForgeElement => {
	return auxNode !== null && typeof auxNode !== 'boolean' && auxNode !== undefined;
};

export type ForgeNode = ForgeElement | null | boolean | undefined;

export type ForgeChildren<T = ForgeNode> = T | (T | T[])[];

export interface PlatformContext {
	type: string;
}

export interface ExtensionContext {
	[key: string]: any;
	type: string;
}

export type CapabilitySetType = 'capabilityStandard' | 'capabilityAdvanced';

export enum EcosystemLicenseMode {
	USER_ACCESS = 'USER_ACCESS',
}
export interface LicenseDetails {
	active: boolean;
	billingPeriod: string;
	capabilitySet: CapabilitySetType;
	ccpEntitlementId: string;
	ccpEntitlementSlug: string;
	isEvaluation: boolean;
	modes: [EcosystemLicenseMode] | null;
	subscriptionEndDate: string | null;
	supportEntitlementNumber: string | null;
	trialEndDate: string | null;
	type: string;
}

export interface UserAccessDetails {
	enabled: boolean;
	hasAccess: boolean;
}

export type AccountType = 'licensed' | 'unlicensed' | 'customer' | 'anonymous';

export interface ProductContext {
	accountId?: string;
	accountType?: AccountType;
	appId?: string;
	cloudId?: string;
	contentId?: string;
	environmentId?: string;
	environmentType?: string;
	extensionContext?: ExtensionContext;
	installContext?: string;
	isConfig?: boolean;
	license?: LicenseDetails;
	localId?: string;
	moduleKey?: string;
	platformContext?: PlatformContext;
	siteUrl?: string;
	spaceId?: string;
	spaceKey?: string;
	workspaceId?: string;
}

export interface ExtensionConfiguration {
	[key: string]: any;
}

export interface User {
	accountId: string;
}

export type CustomFieldValue = string | number | User | null;

export interface JiraContext extends PlatformContext {
	issueId?: number;
	issueKey?: string;
	issueType?: string;
	projectId?: string;
	projectKey?: string;
	projectType?: string;
	type: 'jira';
}

export const isJiraContext = (context: PlatformContext): context is JiraContext =>
	context.type === 'jira';

export interface IssuePanelExtensionContext extends ExtensionContext {
	isNewToIssue: boolean;
	type: 'issuePanel';
}

export const isIssuePanelExtensionContext = (
	extensionContext: ExtensionContext,
): extensionContext is IssuePanelExtensionContext => extensionContext.type === 'issuePanel';

export interface CustomFieldContextConfigExtensionContext extends ExtensionContext {
	configuration?: any;
	contextId: number;
	fieldId: string;
	fieldType: string;
	schema?: { [key: string]: any };
	type: 'contextConfig';
}

export const isCustomFieldContextConfigExtensionContext = (
	extensionContext: ExtensionContext,
): extensionContext is CustomFieldContextConfigExtensionContext =>
	extensionContext.type === 'contextConfig';

export interface CustomFieldExtensionContext extends ExtensionContext {
	fieldId: string;
	fieldType: string;
	fieldValue: CustomFieldValue;
	isInline?: boolean;
	type: 'customField' | 'customFieldType';
}

export const isCustomFieldExtensionContext = (
	extensionContext: ExtensionContext,
): extensionContext is CustomFieldExtensionContext =>
	extensionContext.type === 'customField' || extensionContext.type === 'customFieldType';

export interface ContextMenuExtensionContext extends ExtensionContext {
	selectedText: string;
	type: 'contextMenu';
}

export interface ContentActionExtensionContext extends ExtensionContext {
	type: 'contentAction';
}

export const isContextMenuExtensionContext = (
	extensionContext: ExtensionContext,
): extensionContext is ContextMenuExtensionContext => extensionContext.type === 'contextMenu';

export const isContentActionExtensionContext = (
	extensionContext: ExtensionContext,
): extensionContext is ContentActionExtensionContext => extensionContext.type === 'contentAction';

export interface DashboardGadgetExtensionContext extends ExtensionContext {
	gadgetConfiguration: Record<string, any>;
	type: 'dashboardGadget';
}

export const isDashboardGadgetExtensionContext = (
	extensionContext: ExtensionContext,
): extensionContext is DashboardGadgetExtensionContext =>
	extensionContext.type === 'dashboardGadget';

export enum CompassContextTypes {
	AdminPage = 'compass:adminPage',
	ComponentPage = 'compass:componentPage',
	TeamPage = 'compass:teamPage',
	ComponentImporter = 'compass:componentImporter',
}

export enum RenderingLocations {
	AppConfigurationPage = 'appConfigurationPage',
	OnboardingFlow = 'onboardingFlow',
}

export interface CompassComponentPageExtensionContext extends ExtensionContext {
	componentId: string;
	type: CompassContextTypes.ComponentPage;
}

export interface CompassAdminPageExtensionContext extends ExtensionContext {
	renderingLocation: string;
	type: CompassContextTypes.AdminPage;
	url: string;
}

export interface CompassComponentImporterExtensionContext extends ExtensionContext {
	renderingLocation: string;
	type: CompassContextTypes.ComponentImporter;
}

export interface CompassTeamPageExtensionContext extends ExtensionContext {
	teamId: string;
	type: CompassContextTypes.TeamPage;
}
